import { Injectable, NgZone } from "@angular/core";
import {
  NbWindowService,
  NbComponentType,
  NbWindowRef,
  NbToastrService,
  NbDialogService,
  NbDialogRef,
} from "@nebular/theme";
import { BehaviorSubject, Subject } from "rxjs";
import { AuthUserService } from "../../../@core/data/auth-user.service";
import { BackPage } from "./types/back-page.interface";
import { SharedAnnualLeavingService } from "../services/shared-annual-leaving.service";
import { Router } from "@angular/router";
import {
  OverlayShellContext,
  OverlayShellConfig,
} from "../overlay-shell/overlay.models";
import { OverlayShellComponent } from "../overlay-shell/overlay-shell.component";
import { config } from "process";

@Injectable({
  providedIn: "root",
})
export class TableService {
  public zoom$ = new BehaviorSubject<number>(1.0);
  private toSaveCounter: number = 0;
  private value: any;
  private windowRef: NbWindowRef;
  private dialogRef: NbDialogRef<any>;
  private refreshSource = new Subject<any>();
  private refreshItemOnTable = new Subject<any>();
  private resetSort = new Subject<void>();
  public refresh$ = this.refreshSource.asObservable();
  public refreshLocal$ = this.refreshItemOnTable.asObservable();
  public resetSort$ = this.resetSort.asObservable();
  public offsetTop = 0;
  private backStack: Array<BackPage> = [];
  private windowBeforeParams;
  public isWindowCurrentlyOpen: boolean;

  public isMessageReport: boolean;

  // additional for open window from other component and cannot use backstact because don't know params
  private goToWindowBefore = false;

  public setMainFocus = new BehaviorSubject<void>(null);

  constructor(
    private windowService: NbWindowService,
    private dialogService: NbDialogService,
    private toastrService: NbToastrService,
    private authUserService: AuthUserService,
    private router: Router,
    private ngZone: NgZone,
    private _sharedAnnualLeavingService: SharedAnnualLeavingService
  ) {}

  toSaveCounterAdd() {
    this.toSaveCounter++;
  }

  toSaveCounterDesc() {
    if (this.toSaveCounter > 0) {
      this.toSaveCounter--;
    }
  }

  getToSaveCounter() {
    return this.toSaveCounter;
  }

  setValue(value: any) {
    this.value = value;
  }

  getValue() {
    return this.value;
  }

  reloadTable(parameter: any = true) {
    this.refreshSource.next(parameter);
  }

  resetSortTable() {
    this.resetSort.next();
  }

  openWindowWithoutCareHomeVerification(
    windowContent: NbComponentType,
    title: string,
    config: OverlayShellConfig,
    parameter?: any,
    windowClass?: string
  ) {
    this.openNewWindow(
      windowContent,
      title,
      config,
      false,
      parameter,
      windowClass
    );
  }

  openWindowAndRememberWindowBefore(
    windowContent: NbComponentType,
    title: string,
    config: OverlayShellConfig,
    parameter?: any
  ) {
    this.windowBeforeParams = {
      windowContent,
      title,
      verifyCareHomeSelected: true,
      parameter,
    };
    this.goToWindowBefore = true;
    this.openNewWindow(windowContent, title, config, true, parameter);
  }

  openNewWindowAndCloseThis(
    windowContent: NbComponentType,
    title: string,
    config: OverlayShellConfig,
    parameters?: any
  ) {
    if (this.windowRef) {
      this.windowRef.close();
    }
    this.openNewWindow(
      windowContent,
      title,
      config,
      true,
      parameters,
      undefined
    );
  }

  openWindowWithBackStack(
    windowContent: NbComponentType,
    title: string,
    config: OverlayShellConfig,
    backPage: BackPage,
    parameters?: any,
    windowClass?: string
  ) {
    if (this.windowRef) {
      this.windowRef.close();
    }
    this.openNewWindow(
      windowContent,
      title,
      config,
      true,
      parameters,
      windowClass
    );
    this.backStack.push(backPage);
  }

  openWindow(
    windowContent: NbComponentType,
    title: string,
    config: OverlayShellConfig,
    parameter?: any,
    windowClass?: string
  ) {
    this.openNewWindow(
      windowContent,
      title,
      config,
      true,
      parameter,
      windowClass
    );
  }

  updateLocalObjectAndCloseWindow(objectToUpdate: any, setMainFocus = false) {
    this.refreshItemOnTable.next(objectToUpdate);
    this.closeWindow(false);
    if (setMainFocus) {
      this.setMainFocus.next();
    }
  }

  closeWindowClearAndRedirectTo(url: string, newTab: boolean = false) {
    this.goToWindowBefore = false;
    this.windowBeforeParams = {};
    this.windowRef.close();
    const urlPath = this.router.serializeUrl(this.router.createUrlTree([url]));
    if (newTab) {
      window.open("#" + urlPath);
    } else {
      this.router.navigate([urlPath]);
    }
  }

  justRefreshData(parameter: any = {}) {
    this.refreshSource.next(parameter);
  }

  closeWindow(
    parentPageNeedRefresh: boolean,
    skipPage: number = 0,
    parameter: any = {}
  ) {
    if (parentPageNeedRefresh) {
      this.refreshSource.next(parameter);
    }
    this.windowRef.close();
    this.isWindowCurrentlyOpen = false;
    if (this.goToWindowBefore && this.windowBeforeParams) {
      this.openNewWindow(
        this.windowBeforeParams.windowContent,
        this.windowBeforeParams.title,
        this.windowBeforeParams.verifyCareHomeSelected,
        this.windowBeforeParams.parameter
      );
      this.goToWindowBefore = false;
    }
    if (this.backStack.length > 0) {
      let backPage = this.backStack.pop();
      for (let i = 0; i < skipPage; i++) {
        backPage = this.backStack.pop();
      }
      if (
        backPage &&
        (backPage.elementId !== undefined || backPage.elementUid !== undefined)
      ) {
        this._sharedAnnualLeavingService.elementId = backPage.elementId;
        this._sharedAnnualLeavingService.elementUid = backPage.elementUid;
        this._sharedAnnualLeavingService.eventNo = backPage.eventNo;
        this._sharedAnnualLeavingService.isAl = backPage.isAl;
        this.openNewWindow(
          backPage.component,
          backPage.title,
          backPage.config,
          true,
          backPage,
          undefined
        );
      }

      if (this.backStack.length === 0) {
        this._sharedAnnualLeavingService.elementId = undefined;
        this._sharedAnnualLeavingService.elementUid = undefined;
        this._sharedAnnualLeavingService.eventNo = undefined;
      }
    }
  }

  private openNewWindow(
    windowContent: NbComponentType,
    title: string,
    shellConfig: OverlayShellConfig,
    verifyCareHomeSelected: boolean,
    parameter?: any,
    windowClass?: string
  ) {
    if (verifyCareHomeSelected && this.authUserService.getCareHomeId() == 0) {
      this.toastrService.warning(
        "To do this operation you must choose specific Care Home in header of the page",
        "Info"
      );
    } else {
      this.setValue(parameter);

      const nbConfig = {
        closeOnBackdropClick: windowClass === "max-100-h" ? true : false,
        closeOnEsc: false,
        title: title,
        windowClass: windowClass ? windowClass : "",
      };

      const overlayShellContext: OverlayShellContext = {
        config: shellConfig,
        component: windowContent,
        componentContext: parameter,
      };

      this.windowRef = this.windowService.open(OverlayShellComponent, {
        ...nbConfig,
        context: overlayShellContext,
      });

      this.isWindowCurrentlyOpen = true;
      this.ngZone.runOutsideAngular(() => {
        (<any>document.getElementsByTagName("nb-window")[0]).style.zoom =
          this.zoom$.getValue();
      });

      this.ngZone.runOutsideAngular(() => {
        (<any>document.getElementsByTagName("nb-window")[0]).setAttributeNS(
          null,
          "cdkDrag",
          ""
        );
      });
    }
  }

  public changeWindowName(title: string) {
    this.windowRef.config.title = title;
  }
}
